import { createContext, useContext, useState, ReactNode } from "react";
import {
  DynamicContextProvider,
  useDynamicContext,
  UserProfile,
  useUserWallets,
  Wallet,
} from "@dynamic-labs/sdk-react-core";
import { DynamicWagmiConnector } from "@dynamic-labs/wagmi-connector";
import { createConfig, WagmiProvider } from "wagmi";
import { http, createClient, Chain } from "viem";
import { EthereumWalletConnectors } from "@dynamic-labs/ethereum";
import { ZeroDevSmartWalletConnectors } from "@dynamic-labs/ethereum-aa";
import { evmNetworks, wagmiChains } from "../api/config/networks";
import { useNavigate } from "react-router-dom";
import { getDynamicUser } from "../api";

interface DynamicContextType {
  user: UserObject | null;
  setUser: (user: UserObject | null) => void;
  isLoadingUser: boolean;
  setIsLoadingUser: (loading: boolean) => void;
  primaryWallet: Wallet | null;
  userWallets: Wallet[];
}

const DynamicContext = createContext<DynamicContextType | undefined>(undefined);

// Wagmi configuration
const config = createConfig({
  chains: wagmiChains as [Chain, ...Chain[]],
  client({ chain }) {
    return createClient({
      chain,
      transport: http(),
    });
  },
});

interface DynamicProviderProps {
  children: ReactNode;
}

export const DynamicProvider = ({ children }: DynamicProviderProps) => {
  const [user, setUser] = useState<UserObject | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const navigate = useNavigate();

  const fetchUserDynamic = async (dynamicUser: UserProfile) => {
    try {
      setIsLoadingUser(true);
      const data = await getDynamicUser(dynamicUser);
      setUser(data);
      navigate("/my-projects");
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoadingUser(false);
    }
  };

  return (
    <DynamicContextProvider
      settings={{
        environmentId: `${import.meta.env.VITE_DYNAMIC_ENVIRONMENT_ID}`,
        walletConnectors: [
          EthereumWalletConnectors,
          ZeroDevSmartWalletConnectors,
        ],
        events: {
          onAuthSuccess: (args) => {
            fetchUserDynamic(args.user);
          },
        },
        overrides: { evmNetworks },
      }}
    >
      <WagmiProvider config={config}>
        <DynamicWagmiConnector>
          <DynamicUserStateProvider
            user={user}
            setUser={setUser}
            isLoadingUser={isLoadingUser}
            setIsLoadingUser={setIsLoadingUser}
          >
            {children}
          </DynamicUserStateProvider>
        </DynamicWagmiConnector>
      </WagmiProvider>
    </DynamicContextProvider>
  );
};

interface DynamicUserStateProviderProps {
  children: ReactNode;
  user: UserObject | null;
  setUser: (user: UserObject | null) => void;
  isLoadingUser: boolean;
  setIsLoadingUser: (loading: boolean) => void;
}

const DynamicUserStateProvider = ({
  children,
  user,
  setUser,
  isLoadingUser,
  setIsLoadingUser,
}: DynamicUserStateProviderProps) => {
  const { primaryWallet } = useDynamicContext();
  const userWallets = useUserWallets();

  const value = {
    user,
    setUser,
    isLoadingUser,
    setIsLoadingUser,
    primaryWallet,
    userWallets,
  };

  return (
    <DynamicContext.Provider value={value}>{children}</DynamicContext.Provider>
  );
};

export const useDynamic = () => {
  const context = useContext(DynamicContext);
  if (!context) {
    throw new Error("useDynamic must be used within a DynamicProvider");
  }
  return context;
};
